import React from 'react';
import { cn } from '../../lib/utils';

// ── FancyYool accent → CSS var mapping ──────────────────────────────────────
const FY = {
  teal:   '--fy-teal',
  cyan:   '--fy-cyan',
  blue:   '--fy-blue',
  sky:    '--fy-sky',
  violet: '--fy-violet',
  indigo: '--fy-indigo',
  purple: '--fy-purple',
  pink:   '--fy-pink',
  red:    '--fy-red',
  orange: '--fy-orange',
  lime:   '--fy-lime',
  slate:  '--fy-slate',
  gray:   '--fy-gray',
};

function Sparkline({ data, accentVar }) {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const W = 80, H = 28;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * W},${H - ((v - min) / range) * H}`)
    .join(' ');
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="overflow-visible">
      <polyline
        fill="none"
        stroke={accentVar ? `var(${accentVar})` : 'var(--fy-cyan)'}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
    </svg>
  );
}

function DeltaArrow({ dir }) {
  if (dir === 'up') {
    return (
      <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
        <polygon points="5,1 9,9 1,9" />
      </svg>
    );
  }
  if (dir === 'down') {
    return (
      <svg className="w-2.5 h-2.5" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
        <polygon points="5,9 9,1 1,1" />
      </svg>
    );
  }
  return null;
}

// ── KpiCard ──────────────────────────────────────────────────────────────────
// Componente canónico único para métricas KPI. Fuente de verdad: Capa A
// (yoolColors/yoolBorders/yoolShadows/shadcnTokens + clases Tailwind).
// Sin hex crudos, sin blur/translate/glow, sin Capa B ni Capa C.
export function KpiCard({
  // spec API
  label,
  value,
  prefix,
  suffix,
  icon: IconProp,
  accent,
  delta,
  context,
  spark,
  tone = 'light',
  variant = 'default',
  alert: isAlert = false,
  onClick,
  loading = false,
  className,
  // backward-compat con section-cards badge API
  badge,
}) {
  const accentVar = accent ? FY[accent] : null;

  // Normaliza delta: acepta formato nuevo {dir,value} o legacy badge {direction,text}
  const resolvedDelta = delta ?? (badge ? {
    dir:   badge.direction === 'up' ? 'up' : badge.direction === 'down' ? 'down' : 'flat',
    value: badge.text,
  } : undefined);

  const isCompact = variant === 'compact';
  const isDark    = tone === 'dark';

  const renderIcon = () => {
    if (!IconProp) return null;

    const iconCls   = 'w-4 h-4';
    const iconStyle = accentVar ? { color: `var(${accentVar})` } : undefined;

    const iconEl = React.isValidElement(IconProp)
      ? React.cloneElement(IconProp, { className: iconCls, style: iconStyle })
      : <IconProp className={iconCls} style={iconStyle} />;

    const chipBg = accentVar
      ? { backgroundColor: `color-mix(in srgb, var(${accentVar}) 12%, transparent)` }
      : undefined;

    return (
      <div className={cn('p-2 rounded-xl', !accentVar && 'bg-muted')} style={chipBg}>
        {iconEl}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'relative border rounded-[14px] transition-shadow shadow-sm',
        isCompact ? 'p-[18px]' : 'p-6',
        isDark
          ? 'border-white/10 text-white'
          : cn('bg-card border-border text-foreground', isAlert && 'border-yool-error'),
        onClick && 'cursor-pointer hover:shadow-md',
        loading && 'animate-pulse opacity-70',
        className
      )}
      style={isDark ? { backgroundColor: 'var(--yool-navy)' } : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Label + icon chip */}
      <div className="flex items-start justify-between mb-1">
        <span className={cn(
          'text-[11px] font-semibold uppercase tracking-[.08em]',
          isDark ? 'text-white/60' : 'text-muted-foreground'
        )}>
          {label}
        </span>
        {renderIcon()}
      </div>

      {/* Value */}
      {loading ? (
        <div className={cn('rounded bg-muted', isCompact ? 'h-7 w-20 mt-1' : 'h-10 w-28 mt-1')} />
      ) : (
        <div className={cn(
          'font-semibold tabular-nums leading-none my-1',
          isCompact ? 'text-[26px] tracking-[-0.01em]' : 'text-[38px] tracking-[-0.02em]',
          isDark ? 'text-white' : 'text-foreground'
        )}>
          {prefix && (
            <span className={cn('text-base font-medium mr-1', isDark ? 'text-white/60' : 'text-muted-foreground')}>
              {prefix}
            </span>
          )}
          {value ?? '—'}
          {suffix && (
            <span className={cn('text-base font-medium ml-1', isDark ? 'text-white/60' : 'text-muted-foreground')}>
              {suffix}
            </span>
          )}
        </div>
      )}

      {/* Delta badge + context */}
      {(resolvedDelta || context) && (
        <div className="flex items-center gap-2 mt-1.5 min-h-[20px]">
          {resolvedDelta && (
            <span className={cn(
              'inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full',
              resolvedDelta.dir === 'up'   && 'bg-yool-success-soft text-yool-success',
              resolvedDelta.dir === 'down' && 'bg-yool-error-soft text-yool-error',
              (!resolvedDelta.dir || resolvedDelta.dir === 'flat') && 'bg-muted text-muted-foreground'
            )}>
              <DeltaArrow dir={resolvedDelta.dir} />
              {resolvedDelta.value}
            </span>
          )}
          {context && (
            <span className={cn('text-[11px]', isDark ? 'text-white/50' : 'text-muted-foreground')}>
              {context}
            </span>
          )}
        </div>
      )}

      {/* Sparkline — trazo acento, sin fill */}
      {spark && spark.length >= 2 && (
        <div className="mt-3">
          <Sparkline data={spark} accentVar={accentVar} />
        </div>
      )}
    </div>
  );
}

// ── KpiGrid ──────────────────────────────────────────────────────────────────
// Grid responsivo 1→2→N cols que mapea items[] → <KpiCard>.
// Reemplaza SectionCards y CompanyMetricsGrid.
// Acepta `cards` (compat legacy SectionCards: {title,value,percentage,trend}).
export function KpiGrid({ items, cards, cols = 4, className }) {
  const raw = (items && items.length > 0) ? items : (cards || []);

  const normalized = raw.map(item => {
    if ('title' in item && !('label' in item)) {
      const trendDir = item.trend === 'up' ? 'up' : item.trend === 'down' ? 'down' : 'flat';
      const resolvedDelta =
        // fancy/KpiGrid shape: delta is a string + trend
        (item.delta && typeof item.delta === 'string')
          ? { dir: trendDir, value: item.delta }
        // section-cards shape: percentage + trend
        : item.percentage
          ? { dir: trendDir, value: item.percentage }
        // description as neutral note
        : item.description
          ? { dir: 'flat', value: item.description }
          : undefined;

      return {
        ...item,
        label:   item.title,
        accent:  item.accent ?? item.color,
        context: item.context ?? item.helper,
        delta:   resolvedDelta,
      };
    }
    return item;
  });

  const gridCols = ({
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  })[cols] ?? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';

  return (
    <div className={cn('grid gap-[14px]', gridCols, className)}>
      {normalized.map((item, i) => (
        <KpiCard key={item.label ?? i} {...item} />
      ))}
    </div>
  );
}

// ── StatCard — alias backward-compat ─────────────────────────────────────────
export function StatCard({ title, value, description, trend, percentage, className }) {
  return (
    <KpiCard
      label={title}
      value={value}
      delta={(percentage || description) ? {
        dir:   trend === 'up' ? 'up' : trend === 'down' ? 'down' : 'flat',
        value: percentage || description,
      } : undefined}
      className={className}
    />
  );
}

export default KpiCard;
